import {logger, Time, Util} from 'yougile-util'
import {DataObjectId, Hub, SandboxTree} from 'yougile-model'
import {Backend} from '../../../../common/backend'
import {Sync} from '../../../client/sync'
import {globals} from '../../../extensions/globals-base'

interface ByUserFilterState {
  assignDate: {
    start?: number
    stop?: number
    stopToday: boolean
  }
  user: DataObjectId[]
  project: DataObjectId[]
}

type FilterType = 'assignDate' | 'user' | 'project' | 'actionType'

const getStoreKey = (companyId: DataObjectId): string =>
  `by-user-filter-${companyId}`

const getDefaultState = (): ByUserFilterState => {
  return {
    assignDate: {
      stopToday: false
    },
    project: [],
    user: []
  }
}

const checkAndActualizeState = (
  box: SandboxTree,
  state: ByUserFilterState
): ByUserFilterState => {
  if (!state?.assignDate || !state.project || !state.user) {
    return getDefaultState()
  }
  const res = Util.clone(state)
  if (
    state.assignDate.stopToday &&
    state.assignDate.start &&
    state.assignDate.stop
  ) {
    const diff = Time.middleOfDay(Time.now()) - state.assignDate.stop
    res.assignDate.start += diff
    res.assignDate.stop += diff
  }
  res.user = res.user.filter(uid => box.isUserInCompany(uid))
  res.project = res.project.filter(prId => {
    const pr = box.get(prId)
    return pr && !pr.deleted && pr.location === box.companyId
  })
  return res
}

export class ByUserFilterNotifier {
  private static changeHandler: () => void = Util.noop
  public static onChange(cb: () => void): void {
    ByUserFilterNotifier.changeHandler = cb
  }
  public static trigger(): void {
    ByUserFilterNotifier.changeHandler()
  }
}

const loadState = (companyId: DataObjectId): ByUserFilterState => {
  const box = Sync.get({companyId})
  const str = Backend.loadLocal(getStoreKey(companyId))
  try {
    return !box || !str
      ? getDefaultState()
      : checkAndActualizeState(box, JSON.parse(str))
  } catch (e) {
    return getDefaultState()
  }
}

const saveState = (companyId: DataObjectId, state: ByUserFilterState) => {
  if (state) {
    Backend.saveLocal(getStoreKey(companyId), JSON.stringify(state))
    ByUserFilterNotifier.trigger()
  }
}

export const ByUserFilter = {
  getDates(): [number, number] | undefined {
    const val = ByUserFilter.getChosen('assignDate' as DataObjectId)
    return val && val.length ? [+val[0], +val[1]] : undefined
  },
  getUsers(): DataObjectId[] {
    return ByUserFilter.getChosen('user' as DataObjectId) as DataObjectId[]
  },
  getProjects(): DataObjectId[] {
    return ByUserFilter.getChosen('project' as DataObjectId) as DataObjectId[]
  },
  getChosen(stickerId: DataObjectId): string[] | undefined {
    const state = loadState(globals.getCompanyId())
    const filterType = stickerId as FilterType
    if (filterType === 'assignDate') {
      const val = state[filterType]
      if (!val?.start || !val.stop) {
        return []
      } else {
        const d1 = Math.min(val.stop, Time.middleOfDay(Time.now()))
        return [val.start + '', d1 + '']
      }
    } else {
      if (filterType === 'actionType' || !state[filterType]) {
        logger.warn('Wrong filter type', filterType)
        return undefined
      } else {
        return state[filterType]
      }
    }
  },
  getTimetrackingBoard(): Hub | undefined {
    return undefined
  },
  /* for user, project and actionType */
  onToggle(stickerId: DataObjectId, value: string): void {
    const companyId = globals.getCompanyId()
    const savedState = loadState(companyId)
    const filterType = stickerId as FilterType
    if (filterType !== 'assignDate' && filterType !== 'actionType') {
      if (!savedState[filterType]) {
        logger.warn('Wrong filter type', filterType)
      }
      const saved = new Set(savedState[filterType] as string[])
      if (saved.has(value)) {
        saved.delete(value)
      } else {
        saved.add(value)
      }
      savedState[filterType] = [...saved] as any[]
      saveState(companyId, savedState)
    }
  },
  setFilterValues(stickerId: DataObjectId, values: string[]): void {
    const companyId = globals.getCompanyId()
    const savedState = loadState(companyId)
    const filterType = stickerId as FilterType
    if (filterType !== 'assignDate' && filterType !== 'actionType') {
      if (!savedState[filterType]) {
        logger.warn('Wrong filter type', filterType)
      }
      savedState[filterType] = values as any[]
      saveState(companyId, savedState)
    }
  },
  /* for date (values = [start, stop]) */
  setFilter(stickerId: DataObjectId, values: string[]): void {
    if (stickerId === 'assignDate') {
      if (!values || values.length === 0) {
        const state = loadState(globals.getCompanyId())
        state.assignDate = {stopToday: false}
        saveState(globals.getCompanyId(), state)
      } else {
        const start = +values[0]
        const stop = +values[1]
        if (start > 0 && stop > 0) {
          const now = Time.now()
          const stopToday = Time.sameDates(now, stop)
          const state = loadState(globals.getCompanyId())
          state.assignDate = {start, stop, stopToday}
          saveState(globals.getCompanyId(), state)
        }
      }
    } else {
      this.setFilterValues(stickerId, values)
    }
  }
}
